import { Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { StatusPill, type StatusPillTone } from './StatusPill';
import {
  CheckmarkCircleFilled,
  WarningFilled,
  ErrorCircleFilled,
} from '@fluentui/react-icons';
import type { QualityStatus } from '../../types';

const useStyles = makeStyles({
  indicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  good: {
    color: tokens.colorPaletteGreenForeground1,
  },
  warning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  poor: {
    color: tokens.colorPaletteRedForeground1,
  },
});

interface QualityIndicatorProps {
  status: QualityStatus;
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
  tooltip?: string;
}

const statusConfig = {
  good: {
    icon: CheckmarkCircleFilled,
    label: 'Good',
    tone: 'positive' as StatusPillTone,
  },
  warning: {
    icon: WarningFilled,
    label: 'Warning',
    tone: 'warning' as StatusPillTone,
  },
  poor: {
    icon: ErrorCircleFilled,
    label: 'Poor',
    tone: 'negative' as StatusPillTone,
  },
};

export function QualityIndicator({
  status,
  showLabel = false,
  size = 'medium',
  tooltip,
}: QualityIndicatorProps) {
  const styles = useStyles();
  const config = statusConfig[status];
  const Icon = config.icon;

  const iconSize = size === 'small' ? 16 : size === 'large' ? 24 : 20;

  const content = (
    <span className={`${styles.indicator} ${styles[status]}`}>
      <Icon style={{ fontSize: iconSize }} />
      {showLabel && <span>{config.label}</span>}
    </span>
  );

  if (tooltip) {
    return (
      <Tooltip content={tooltip} relationship="label">
        {content}
      </Tooltip>
    );
  }

  return content;
}

interface QualityBadgeProps {
  status: QualityStatus;
}

export function QualityBadge({ status }: QualityBadgeProps) {
  const config = statusConfig[status];

  return (
    <StatusPill tone={config.tone}>
      {config.label}
    </StatusPill>
  );
}
